/**
 * Helpers for styling to reduce CSS code complexity
 */

import {
  Breakpoint,
  CommonColors,
  Palette,
  PaletteColor,
  Theme,
  TypeAction,
  TypeBackground,
  TypeText,
} from '@mui/material';

type ThemeHelperProps = {
  theme?: Theme;
};

type Severity = Pick<Palette, 'primary' | 'secondary' | 'info' | 'warning' | 'success' | 'error'>;

const template =
  (type: keyof Severity) =>
  (color: keyof PaletteColor) =>
  ({ theme }: ThemeHelperProps) =>
    theme?.palette?.[type]?.[color] || '';

const text =
  (color: keyof TypeText) =>
  ({ theme }: ThemeHelperProps) =>
    theme?.palette.text[color];

const action =
  (color: keyof TypeAction) =>
  ({ theme }: ThemeHelperProps) =>
    theme?.palette.action[color];

const common =
  (color: keyof CommonColors) =>
  ({ theme }: ThemeHelperProps) =>
    theme?.palette.common[color];

const background =
  (color: keyof TypeBackground) =>
  ({ theme }: ThemeHelperProps) =>
    theme?.palette.background[color];

/**
 * Use colors defined in them
 * */

export const palette = {
  primary: template('primary'),
  secondary: template('secondary'),
  error: template('error'),
  success: template('success'),
  info: template('info'),
  warning: template('warning'),
  action,
  background,
  common,
  text,
};

/**
 * Call for theme breakpoint
 * Shortens a call for theme breakpoint when called from CSS
 */
export const breakpoints = {
  up:
    (breakpoint: Breakpoint) =>
    ({ theme }: ThemeHelperProps) =>
      theme?.breakpoints.up(breakpoint),
  down:
    (breakpoint: Breakpoint) =>
    ({ theme }: ThemeHelperProps) =>
      theme?.breakpoints.down(breakpoint),
  between:
    (min: Breakpoint, max: Breakpoint) =>
    ({ theme }: ThemeHelperProps) =>
      theme?.breakpoints.between(min, max),
};

export const spacing =
  (num: number) =>
  ({ theme }: ThemeHelperProps) =>
    theme?.spacing(num);
